<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-seg" role="tablist" :aria-label="$t('report_mobile.group_by')">
        <button type="button" role="tab" :aria-selected="String(grouping === 'meal')" @click="grouping = 'meal'">
          {{ $t("report_desktop.by_meal") }}
        </button>
        <button type="button" role="tab" :aria-selected="String(grouping === 'company')" @click="grouping = 'company'">
          {{ $t("report_desktop.by_company") }}
        </button>
      </div>
    </report-tools>

    <report-state
      v-if="!selectedCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
    ></report-state>
    <report-state v-else-if="failed" kind="error" @retry="loadMealsForRestaurantCompanies"></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-bowl-mix-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else-if="grouping === 'meal'" class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("meal") }} {{ sortArrow("name") }}</button>
            </th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-right"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">{{ $t(shift.label) }} {{ sortArrow(shift.key) }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }} {{ sortArrow("total") }}</button>
            </th>
            <th scope="col" class="rd-col-share">{{ $t("report_desktop.share") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.id"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row) }"
            @click="openMeal(row, $event)"
            @keydown.enter.prevent="openMeal(row, $event)"
            @keydown.space.prevent="openMeal(row, $event)"
          >
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td
              v-for="shift in visibleShifts"
              :key="shift.id"
              class="is-right rd-num"
              :class="{ 'is-zero': !row[shift.key] }"
            >
              {{ row[shift.key] }}
            </td>
            <td class="is-right rd-num"><b>{{ row.total }}</b></td>
            <td class="rd-col-share">
              <span class="rd-share">
                <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
                <span class="rd-muted rd-num">{{ row.share }}%</span>
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
              {{ totals[shift.key] }}
            </td>
            <td class="is-right rd-num">{{ totals.total }}</td>
            <td class="rd-col-share"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <template v-else>
      <section v-for="section in companySections" :key="section.name">
        <div class="rd-section">
          <h2>{{ section.name }}</h2>
          <span class="rd-num">{{ pluralT("report_mobile.meals_n", section.total) }}</span>
        </div>
        <div class="rd-card">
          <table class="rd-tbl">
            <thead>
              <tr>
                <th scope="col">{{ $t("meal") }}</th>
                <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-right">
                  {{ $t(shift.label) }}
                </th>
                <th scope="col" class="is-right">{{ $t("total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in section.rows" :key="row.id">
                <td class="is-name" :title="row.name">{{ row.name }}</td>
                <td
                  v-for="shift in visibleShifts"
                  :key="shift.id"
                  class="is-right rd-num"
                  :class="{ 'is-zero': !row[shift.key] }"
                >
                  {{ row[shift.key] }}
                </td>
                <td class="is-right rd-num"><b>{{ row.total }}</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsTabMeals from "@/components/report/meals/tabs/ReportMealsTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "../../shared/hasPlural";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import hasTableSort from "../hasTableSort";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import ReportDesktopMealPanel from "./ReportDesktopMealPanel.vue";
import { copyText, rowsToTsv } from "../copyTable";

/**
 * Obroci na računaru: obrok × smena sa udjelom u ukupnom, grupisanje po obroku ili po firmi i
 * panel sa razlaganjem po firmama. Nasleđuje učitavanje, štampu i Excel iz ReportMealsTabMeals.
 */
export default {
  name: "ReportDesktopMeals",
  extends: ReportMealsTabMeals,
  mixins: [hasPlural, hasShiftBinding, hasShiftSubtitle, hasTableSort],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    companyMeals: [],
    failed: false,
    grouping: "meal",
    requestId: 0,
  }),
  computed: {
    /** Smene 1–3 uvek, četvrta samo kad ima obroka. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) &&
          (shift.id < 4 || this.filteredMeals.some((meal) => meal[shift.key] > 0))
      );
    },
    rows() {
      const searched = this.customSearch(this.filteredMeals, [{ value: "name" }], this.filters.search);
      const visible = searched.filter((meal) => meal.total > 0);
      const grand = visible.reduce((sum, meal) => sum + meal.total, 0);
      const max = Math.max(1, ...visible.map((meal) => meal.total));
      return this.sortRows(
        visible.map((meal) => ({
          ...meal,
          share: grand ? Math.round((meal.total / grand) * 100) : 0,
          relative: Math.round((meal.total / max) * 100),
        }))
      );
    },
    totals() {
      const totals = { total: 0 };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + (row[shift.key] || 0), 0);
      });
      totals.total = this.rows.reduce((sum, row) => sum + row.total, 0);
      return totals;
    },
    /** Isti obroci razloženi po firmi (iz istog odgovora; bez dodatnog poziva). */
    companySections() {
      const search = this.filters.search;
      return this.companyMeals
        .map((company) => {
          const rows = this.customSearch(
            company.meals.map((meal) => this.companyMealRow(meal)),
            [{ value: "name" }],
            search
          )
            .filter((row) => row.total > 0)
            .sort((a, b) => b.total - a.total);
          return {
            name: this.toLatin(company.name),
            rows,
            total: rows.reduce((sum, row) => sum + row.total, 0),
          };
        })
        .filter((section) => section.rows.length);
    },
    emptyTitle() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.selectedShifts.length === 1
        ? this.$t("report_mobile.no_meals_in_shift")
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.totals.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
        { key: "companies", label: this.$t("companies"), value: this.selectedCompanies.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    /** Isto kao stari tab, uz razlaganje po firmama i stanje greške. */
    async loadMealsForRestaurantCompanies() {
      const requestId = ++this.requestId;
      this.failed = false;
      if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
        this.meals = [];
        this.companyMeals = [];
        return;
      }
      this.loading = true;
      this.meals = [];
      this.companyMeals = [];

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
        company: this.selectedCompanies.map((company) => company.id).join(","),
      };

      try {
        const data = await reportService.getMealsForRestaurantCompanies(
          this.$store.state.restaurant.id,
          params
        );
        if (requestId !== this.requestId) {
          return;
        }
        const aggregatedMeals = {};
        const companiesArray = Array.isArray(data)
          ? data
          : data?.companies || (typeof data === "object" ? Object.values(data) : []);

        companiesArray.forEach((company) => {
          if (!company.meals || !Array.isArray(company.meals)) return;

          company.meals.forEach((meal) => {
            const mealId = meal.id.toString();
            if (!aggregatedMeals[mealId]) {
              aggregatedMeals[mealId] = {
                id: meal.id,
                name: meal.name,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
                total: 0,
              };
            }
            aggregatedMeals[mealId].shift_one += meal.shift_one?.orders || 0;
            aggregatedMeals[mealId].shift_two += meal.shift_two?.orders || 0;
            aggregatedMeals[mealId].shift_three += meal.shift_three?.orders || 0;
            aggregatedMeals[mealId].shift_four += meal.shift_four?.orders || 0;
            aggregatedMeals[mealId].total += meal.total || 0;
          });
        });

        this.meals = Object.values(aggregatedMeals).map((meal, index) => ({
          ...meal,
          num: index + 1,
        }));
        this.companyMeals = companiesArray.filter((company) => Array.isArray(company.meals));
      } catch (error) {
        if (requestId === this.requestId) {
          this.meals = [];
          this.companyMeals = [];
          this.failed = true;
          this.showErrorSnackbar(error, this.$t("error_loading_meals"));
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    /** Obrok jedne firme: samo izabrane smene i njihov zbir. */
    companyMealRow(meal) {
      const row = { id: meal.id, name: meal.name, total: 0 };
      SHIFTS.forEach((shift) => {
        row[shift.key] = this.selectedShifts.includes(shift.id) ? meal[shift.key]?.orders || 0 : 0;
        row.total += row[shift.key];
      });
      return row;
    },
    /** Razlaganje jednog obroka po firmama, za panel. */
    mealDetail(mealId) {
      const meal = this.meals.find((item) => item.id === mealId);
      if (!meal) {
        return null;
      }
      const companies = this.companyMeals
        .map((company) => {
          const found = company.meals.find((item) => item.id === mealId);
          return found ? { ...this.companyMealRow(found), name: this.toLatin(company.name) } : null;
        })
        .filter((company) => company && company.total > 0)
        .sort((a, b) => b.total - a.total);
      const total = companies.reduce((sum, company) => sum + company.total, 0);
      return { id: mealId, name: meal.name, total, companies, shifts: this.visibleShifts };
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `meal-${row.id}`);
    },
    openMeal(row, event) {
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `meal-${row.id}`,
        root: true,
        title: row.name,
        subtitle: this.pluralT("report_mobile.meals_n", row.total),
        component: ReportDesktopMealPanel,
        props: { owner: this, mealId: row.id },
      });
    },
    async copyTable() {
      let headers;
      let rows;
      if (this.grouping === "meal") {
        headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
        rows = this.rows.map((row) => [
          row.name,
          ...this.visibleShifts.map((shift) => row[shift.key]),
          row.total,
        ]);
      } else {
        headers = [this.$t("firm"), this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
        rows = this.companySections.flatMap((section) =>
          section.rows.map((row) => [
            section.name,
            row.name,
            ...this.visibleShifts.map((shift) => row[shift.key]),
            row.total,
          ])
        );
      }
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
